//@author: devin

import {
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    input,
    Input,
    model,
    NgZone,
    OnInit,
    output,
    Output,
    Renderer2,
    ChangeDetectionStrategy,
} from "@angular/core";
import { isDefined } from "../../util/core";
import { S25Util } from "../../util/s25-util";
import { Api } from "../../api/api";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { BaseControlValueAccessor } from "../reactive-forms-control-value-accessor/base.control.value.accessor.directive";
import { FormValueControl } from "@angular/forms/signals";
import { Nullable } from "../../pojo/Util";
import { AriaLabel } from "../../util/aria/aria.label";
import { InstanceCount } from "../../util/s25.instance.count";

export class EditableApi extends Api {
    public static refresh(editableElem: HTMLElement, uuid?: string) {
        return Api.callApiFn(editableElem, "", "refresh", null, undefined, (comp: any) => {
            return !uuid || (comp && comp.uuid === uuid);
        });
    }
}

@Component({
    template: "",
    host: {
        "[class.editing]": "editing",
        // TODO: style disabled state
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25EditableAbstract<T> extends BaseControlValueAccessor<T> implements OnInit, FormValueControl<T | null> {
    static DEFAULT_MIN: number = 0;
    static DEFAULT_MAX: number = 9999999;
    private static nextFieldId = InstanceCount.idGenerator({ prefix: "s25Editable" });

    readonly min = input(S25EditableAbstract.DEFAULT_MIN, {
        transform: (val: Nullable<number>) => S25Util.coalesce(val, S25EditableAbstract.DEFAULT_MIN),
    });
    readonly max = input(S25EditableAbstract.DEFAULT_MAX, {
        transform: (val: Nullable<number>) => S25Util.coalesce(val, S25EditableAbstract.DEFAULT_MAX),
    });
    readonly disabled = input<boolean>(false);
    @Input() allowEmpty: boolean = true;
    @Input() hasCommit: boolean;
    @Input() hasCancelButton: boolean;
    @Input() hasCommitButton: boolean;
    @Input() alwaysEditing: boolean = false;
    @Input() cancelButtonText: string = "Cancel";
    @Input() commitButtonText: string = "Save";
    @Input() onCommit?: (inputVal: any) => any; //runs once user has committed
    @Input() onUpdate: (inputVal: any) => any; //if NOT hasCommit, this runs on each model val update
    @Input() onBlur: (inputVal: any) => any; //if NOT hasCommit, this runs on blur after val updated
    readonly readOnly = input<boolean>(false);
    // TODO: make `val` type `T`
    /*
     * "val" is the original input and "value" is required for signal form fields.
     * Ideally we would update all editable abstract components to use the "value" input, but that would be a big refactor.
     * For now it works to just keep them in sync; updating the "value" in the setter for "val" and updating "val" from
     * an effect dependent on "value".
     */
    #val: any;
    @Input() set val(val: any) {
        if (val === this.#val) return;
        this.#val = val;
        this.value.set(val);
    }
    get val() {
        return this.#val;
    }
    @Input() customValidation: (inputVal: any) => boolean | string[] | Promise<boolean | string[]>;
    @Input() valOnInit: boolean = false; // run validation on init if true; useful if value is being populated like in copy object
    // TODO: consider creating something like s25Input.uniqueId({prefix: "s25Editable"})
    readonly fieldId = input(S25EditableAbstract.nextFieldId(), {
        transform: (value?: string | null) => value || S25EditableAbstract.nextFieldId(),
    });
    @Input() placeholder?: string; // if no val, option to show placeholder text
    @Input() rows?: number = 2; // rows option for textarea, make it flexible  to  adjust the size
    @Input() cols?: number = 20; // cols option for textarea, make it flexible  to  adjust size
    readonly ariaLabel = input<AriaLabel>();
    @Input() maxHeight?: number; // optional max height for fade preview in pixels when not in edit mode
    @Input() fadeHeight?: number; // optional height for fade preview in pixels when not in edit mode
    @Input() fadePreview?: boolean; // optional fade preview display when not in edit mode
    readonly value = model<T | null>(null);

    readonly valChange = output<any>();
    @Output() blurred = new EventEmitter<string>();
    @Output() disablingError = new EventEmitter<boolean>(); // use to disable buttons in parent component if error (example in s25.copy.object.component.ts)
    @Output() onCancel = new EventEmitter<void>();

    readonly ariaLabelledBy = computed(() => AriaLabel.labelledBy(this.ariaLabel()));
    readonly ariaLabelText = computed(() => AriaLabel.text(this.ariaLabel()));
    candidateVal: any;
    errorMessages: Nullable<string[]>;
    editing: boolean = false;
    committing: boolean = false;
    uuid: string = "editable-" + Date.now() + "-" + Math.floor(Math.random() * 10000);

    readonly elem = inject(ElementRef);
    readonly cdRef = inject(ChangeDetectorRef);
    readonly zoneRef = inject(NgZone);
    readonly elRenderer = inject(Renderer2);

    getType = (): string => {
        throw new Error("Undefined type");
    };

    static mergeValidations = (resp1: boolean | string[], resp2: boolean | string[]) => {
        if (resp1 === true && resp2 === true) {
            return true;
        }

        let errors: string[] = [];
        if (resp1 !== true && S25Util.isDefined(resp1)) {
            errors = errors.concat(S25Util.array.forceArray(resp1) as string[]);
        }
        if (resp2 !== true && S25Util.isDefined(resp2)) {
            errors = errors.concat(S25Util.array.forceArray(resp2) as string[]);
        }
        return errors.filter((err) => !!err);
    };

    refresh = () => {
        this.cdRef.detectChanges();
    };

    validate = async (inputVal: any) => {
        let ret: boolean | string[] = true;
        const isBlank = S25Util.isUndefined(inputVal) || inputVal === "";
        const isInvalidInt = this.getType() === "integer" && !S25Util.isInt(inputVal);
        const isInvalidFloat = ["percentage", "float"].indexOf(this.getType()) > -1 && !S25Util.isFloat(inputVal);
        const inputValSize =
            ["integer", "percentage", "float"].indexOf(this.getType()) > -1
                ? parseFloat(inputVal)
                : ["text", "textarea", "richtext"].indexOf(this.getType()) > -1
                  ? S25Util.toStr(inputVal).length
                  : null;

        if (isBlank) {
            if (!this.allowEmpty) ret = ["Please enter a non-blank value."];
        } else if (isInvalidInt || isInvalidFloat) {
            ret = ["Please enter a valid " + this.getType() + "."];
        } else if (isDefined(inputValSize) && inputValSize < this.min()) {
            ret = ["Please enter a " + this.getType() + " size greater than or equal to " + this.min() + "."];
        } else if (isDefined(inputValSize) && inputValSize > this.max()) {
            ret = ["Please enter a " + this.getType() + " size less than or equal to " + this.max() + "."];
        }

        if (this.customValidation) {
            const submitButton = this.elem.nativeElement.querySelector(".aw-button.aw-button--primary");
            submitButton && S25LoadingApi.init(submitButton);
            const error = await this.customValidation(inputVal);
            submitButton && S25LoadingApi.destroy(submitButton);
            const mergedErrors = S25EditableAbstract.mergeValidations(ret, error);
            if (Array.isArray(mergedErrors) && mergedErrors.length) {
                return mergedErrors;
            }
            return true;
        } else {
            return ret;
        }
    };

    //update candidate and run validation (optionally)
    _update = async (candidateVal: any, skipValidate?: boolean) => {
        this.candidateVal = candidateVal;
        this.errorMessages = null;

        if (!skipValidate) {
            const validation = await this.validate(this.candidateVal);
            if (validation === true) {
                this.val = this.candidateVal;
                this.getType() === "duration"
                    ? this.valChange.emit(S25Util.daysHoursMinutesToDuration(this.val))
                    : this.valChange.emit(this.val);
                this.disablingError.emit(false);
            } else {
                this.errorMessages = validation as string[];
                if (this.errorMessages) {
                    this.disablingError.emit(true);
                }
            }

            this.onTouched();
            this.onValChange(this.val);
            this.cdRef.detectChanges();

            return validation;
        }
    };

    //update model: if hasCommit, just update candidate and save validation for later; else run validation too and onUpdate func
    update = async (candidateVal: any, fromBlur?: boolean, duration?: any) => {
        if (duration === "days") {
            this.candidateVal.days = candidateVal;
        } else if (duration === "hours") {
            this.candidateVal.hours = candidateVal;
        } else if (duration === "minutes") {
            this.candidateVal.minutes = candidateVal;
        }
        if (this.getType() === "duration") {
            this.val = S25Util.daysHoursMinutesToDuration(this.candidateVal);
            candidateVal = this.candidateVal;
        }

        if (this.hasCommit) {
            this._update(candidateVal, true);
        } else {
            const validation = await this._update(candidateVal);
            if (validation === true) {
                this.onUpdate && this.onUpdate(this.val);
                fromBlur && this.onBlur && this.onBlur(this.val);
                if (fromBlur) this.blurred.emit(this.val);
            }
        }
    };

    commit = async () => {
        this.committing = true;
        const validation = await this._update(this.candidateVal);
        if (validation === true) {
            if (this.getType() === "duration") this.val = S25Util.daysHoursMinutesToDuration(this.val);
            this.onCommit && this.onCommit(this.val);
            this.close();
        }
        setTimeout(() => {
            this.committing = false;
            this.cdRef.detectChanges();
        }, 10);
    };

    //either commit or update and close
    submit = (candidateVal?: any, fromBlur?: boolean) => {
        if (this.hasCommit) {
            this.commit();
        } else {
            this.update(candidateVal || this.candidateVal, fromBlur);
            this.close();
        }
    };

    close = () => {
        this.editing = false || this.alwaysEditing;
        this.cdRef.detectChanges();
    };

    cancel = () => {
        this.candidateVal = this.val;
        this.onCancel.emit();
        this.close();
    };

    onInputBlur = () => {
        setTimeout(() => {
            if (!this.committing) {
                if (this.hasCommit && !this.elem.nativeElement.contains(document.activeElement)) {
                    if (this.hasCommit && this.candidateVal !== this.val) {
                        this.submit(this.candidateVal, true);
                    } else {
                        this.cancel();
                    }
                } else if (!this.hasCommit) {
                    this.submit(this.candidateVal, true);
                }
            }
            this.cdRef.markForCheck(); // add for host binding to reflect changes
        }, 1);
    };

    editMode = (event?: Event) => {
        if (this.disabled() || this.readOnly()) {
            console.warn(
                `Attempted to edit a ${[this.disabled() ? "disabled" : "", this.readOnly() ? "readonly" : ""]
                    .filter(Boolean)
                    .join(" and ")} editable component.`,
            );
            return;
        }
        const editableType = this.getType();
        // user has highlighted text, avoid mode change to allow for copying/pasting
        if (["textarea", "richtext"].includes(editableType) && S25Util.isTextHighlight()) {
            return;
        }
        // prevent bubbling, e.g. space jumping down the page
        event?.preventDefault();
        this.editing = !this.editing;
        this.cdRef.detectChanges();
        this.zoneRef.run(() => {
            const elem = this.elem.nativeElement.querySelector(".firstFocusEditable");
            elem?.focus();
            if (editableType === "textarea" && this.fadePreview) {
                setTimeout(() => {
                    this.elRenderer.setStyle(elem, "height", `${elem?.scrollHeight}px`);
                });
            }
        });
    };

    constructor() {
        super();
        this.elem.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns

        effect(() => {
            //Treat nullish as equal so update doesn't run when switching from undefined to null on init
            if (this.val == null && this.value() == null) return;
            if (this.val !== this.value()) this.update(this.value());
        });
    }

    ngOnInit() {
        this.getType() === "duration"
            ? (this.candidateVal = S25Util.ISODurationToObj(this.val))
            : (this.candidateVal ??= this.val);

        if (this.alwaysEditing) {
            this.hasCommit = false;
            this.editing = true;
        }

        if (this.val && this.candidateVal && this.valOnInit) {
            this.update(this.candidateVal);
        }

        if (this.hasCommit) {
            if (this.hasCommitButton !== false) {
                this.hasCommitButton = true;
            }

            if (this.hasCancelButton !== false) {
                this.hasCancelButton = true;
            }
        } else {
            this.hasCommitButton = false;
            this.hasCancelButton = false;
        }

        this.cdRef.detectChanges();
    }

    onWriteValue(value: T | null) {
        this.val = value;
        if (this.getType() !== "duration") this.candidateVal = value;
        this.cdRef.detectChanges();
    }
}
